/**
 * Store Finder — store-finder.js
 * Place in: assets/store-finder.js
 *
 * Depends on: Leaflet 1.9.4 (self-hosted in assets/, loaded on demand),
 *             postcodes.io (free UK geocoding, no API key)
 *
 * Leaflet is not loaded until the section scrolls into view, so pages that
 * carry a store finder below the fold pay nothing for it.
 */

'use strict';

// ── Registry so we can destroy + reinit on theme editor reloads ──
const storeFinderInstances = new Map();

// ── Leaflet loader: one request per page, however many sections ──
let leafletPromise = null;

function loadLeaflet(jsUrl, cssUrl) {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    if (cssUrl && !document.querySelector('link[data-leaflet-style]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      link.setAttribute('data-leaflet-style', '');
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = jsUrl;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error(`Could not load Leaflet from ${jsUrl}`));
    document.head.appendChild(script);
  });

  return leafletPromise;
}

class StoreFinder {
  constructor(sectionId, data, el) {
    this.sectionId = sectionId;
    this.data = data;
    this.stores = data.stores.map(s => ({ ...s })); // shallow clone

    this.el = el || document.getElementById(`store-finder-${sectionId}`);
    if (!this.el) return;

    this.mapEl = document.getElementById(`store-finder-map-${sectionId}`);
    this.listEl = this.el.querySelector('.store-finder__list');
    this.countEl = this.el.querySelector('.store-finder__list-count');
    this.noticeEl = this.el.querySelector('.store-finder__notice');

    // Search controls (may not exist if disabled in settings)
    this.searchInput = this.el.querySelector('.store-finder__search-input');
    this.searchBtn = this.el.querySelector('[data-search-btn]');
    this.searchClear = this.el.querySelector('.store-finder__search-clear');
    this.searchError = this.el.querySelector('.store-finder__search-error');

    this.map = null;
    this.markers = {}; // index → Leaflet marker
    this.userMarker = null;
    this.destroyed = false;

    this._init();
  }

  async _init() {
    // Geocoding and the Leaflet download are independent — run them together.
    const [leaflet] = await Promise.all([
      loadLeaflet(this.el.dataset.leafletJs, this.el.dataset.leafletCss).catch(err => {
        console.warn('[StoreFinder]', err.message);
        return null;
      }),
      this._geocodeStores(),
    ]);

    if (this.destroyed) return;

    this._bindEvents();
    this._reportUnresolved();
    this._updateCount(this._validStores().length);

    if (leaflet) this._initMap();
  }

  // ── Geocoding ───────────────────────────────────────────────

  /**
   * Resolve postcodes to coordinates, preferring (1) coordinates entered in the
   * theme editor, (2) a cached lookup, (3) a live postcodes.io call.
   */
  async _geocodeStores() {
    const pending = this.stores.filter(s => !this._hasCoords(s) && s.postcode);
    if (!pending.length) return;

    const cache = this._readCache();
    const uncached = [];

    pending.forEach(store => {
      const hit = cache[this._normalise(store.postcode)];
      if (hit) {
        store.lat = hit.lat;
        store.lng = hit.lng;
      } else {
        uncached.push(store);
      }
    });

    if (!uncached.length) return;

    // postcodes.io rejects more than 100 postcodes in one bulk request.
    for (let i = 0; i < uncached.length; i += StoreFinder.BULK_LIMIT) {
      await this._geocodeBatch(uncached.slice(i, i + StoreFinder.BULK_LIMIT), cache);
    }

    this._writeCache(cache);
  }

  async _geocodeBatch(batch, cache) {
    try {
      const res = await fetch('https://api.postcodes.io/postcodes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcodes: batch.map(s => s.postcode) }),
      });

      if (!res.ok) throw new Error(`postcodes.io returned ${res.status}`);

      const { result } = await res.json();

      // Match on the echoed query, not array position — safer if the API ever
      // reorders, and it handles duplicate postcodes correctly.
      const found = {};
      (result || []).forEach(item => {
        if (item && item.result) found[this._normalise(item.query)] = item.result;
      });

      batch.forEach(store => {
        const hit = found[this._normalise(store.postcode)];
        if (!hit) return;

        store.lat = hit.latitude;
        store.lng = hit.longitude;
        cache[this._normalise(store.postcode)] = {
          lat: hit.latitude,
          lng: hit.longitude,
          t: Date.now(),
        };
      });
    } catch (err) {
      console.warn('[StoreFinder] Geocoding batch failed:', err);
    }
  }

  _readCache() {
    try {
      const parsed = JSON.parse(localStorage.getItem(StoreFinder.CACHE_KEY)) || {};
      const cutoff = Date.now() - StoreFinder.CACHE_TTL;
      const fresh = {};

      Object.keys(parsed).forEach(key => {
        const entry = parsed[key];
        if (entry && entry.t > cutoff) fresh[key] = entry;
      });

      return fresh;
    } catch {
      return {}; // private browsing, blocked storage, corrupted value
    }
  }

  _writeCache(cache) {
    try {
      localStorage.setItem(StoreFinder.CACHE_KEY, JSON.stringify(cache));
    } catch {
      /* storage unavailable — geocoding still works, it just won't persist */
    }
  }

  /** Anything that couldn't be placed is still listed, but say so. */
  _reportUnresolved() {
    if (!this.noticeEl) return;

    const missing = this.stores.filter(s => !this._hasCoords(s)).length;

    if (!missing) {
      this.noticeEl.hidden = true;
      return;
    }

    this.noticeEl.textContent =
      missing === 1
        ? 'One stockist could not be placed on the map. It is still listed below.'
        : `${missing} stockists could not be placed on the map. They are still listed below.`;
    this.noticeEl.hidden = false;
  }

  // ── Map init ────────────────────────────────────────────────

  _initMap() {
    if (!this.mapEl || !window.L) return;

    const tileConfig = this._tileConfig(this.data.mapTile);
    const validStores = this._validStores();

    // Default centre: middle of UK
    const center = [54.5, -3];
    const zoom = this.data.defaultZoom || 6;

    this.map = window.L.map(this.mapEl, {
      center,
      zoom,
      scrollWheelZoom: false,
      zoomControl: true,
    });

    window.L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      subdomains: tileConfig.subdomains || 'abc',
      maxZoom: 19,
    }).addTo(this.map);

    validStores.forEach(store => this._addMarker(store));

    // Fit map to show all pins
    if (validStores.length > 1) {
      this.map.fitBounds(this._bounds(validStores), { padding: [40, 40] });
    } else if (validStores.length === 1) {
      this.map.setView(
        [parseFloat(validStores[0].lat), parseFloat(validStores[0].lng)],
        12
      );
    }
  }

  _addMarker(store) {
    const latlng = [parseFloat(store.lat), parseFloat(store.lng)];
    const icon = this._markerIcon();

    const marker = window.L.marker(latlng, { icon, title: store.name });

    marker.bindPopup(this._popupHTML(store), {
      maxWidth: 260,
      className: 'store-finder__popup',
    });

    marker.on('click', () => {
      this._setActiveListItem(store.index);
    });

    marker.addTo(this.map);
    this.markers[store.index] = marker;
  }

  _markerIcon() {
    const color = this.data.markerColor || '#000000';
    // SVG pin: filled with theme color, white inner circle
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36" aria-hidden="true">
      <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24S24 21 24 12C24 5.373 18.627 0 12 0z" fill="${color}"/>
      <circle cx="12" cy="12" r="4.5" fill="white"/>
    </svg>`;

    return window.L.divIcon({
      html: svg,
      className: 'store-finder__marker',
      iconSize: [24, 36],
      iconAnchor: [12, 36],
      popupAnchor: [0, -38],
    });
  }

  _popupHTML(store) {
    const addressParts = [
      store.address_1,
      store.address_2,
      store.town,
      store.county,
      store.postcode,
    ].filter(Boolean);

    let html = `<div class="store-popup">`;
    html += `<strong class="store-popup__name heading h5">${this._esc(store.name)}</strong>`;

    if (addressParts.length) {
      html += `<p class="store-popup__address">${addressParts.map(p => this._esc(p)).join('<br>')}</p>`;
    }

    html += `<div class="store-popup__links">`;

    if (store.phone) {
      const tel = store.phone.replace(/\s/g, '');
      html += `<a href="tel:${this._esc(tel)}" class="store-popup__link">${this._esc(store.phone)}</a>`;
    }

    if (this._safeUrl(store.website)) {
      const label = store.website
        .replace(/^https?:\/\/(www\.)?/, '')
        .replace(/\/$/, '');
      html += `<a href="${this._esc(store.website)}" target="_blank" rel="noopener noreferrer" class="store-popup__link">${this._esc(label)}</a>`;
    }

    if (store.instagram) {
      // Strip anything that isn't a handle — merchants paste full URLs and @ signs
      const handle = String(store.instagram).replace(/^.*instagram\.com\//, '').replace(/[^A-Za-z0-9._]/g, '');
      if (handle) {
        html += `<a href="https://instagram.com/${this._esc(handle)}" target="_blank" rel="noopener noreferrer" class="store-popup__link">@${this._esc(handle)}</a>`;
      }
    }

    html += `</div></div>`;
    return html;
  }

  // ── Tile configs ────────────────────────────────────────────

  _tileConfig(tile) {
    const osm = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors';
    const carto = `${osm} &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>`;

    const key = (this.data.cartoApiKey || '').trim();

    // CARTO basemaps are no longer anonymous — the key goes on the tile request
    // as ?key=… (see carto.com/basemaps/apikey). Subdomains a-d, {r} -> @2x.
    const cartoUrl = style =>
      `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png` +
      `?key=${encodeURIComponent(key)}`;

    const configs = {
      osm: {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: osm,
      },
      carto_light: {
        url: cartoUrl('light_all'),
        attribution: carto,
        subdomains: 'abcd',
      },
      carto_dark: {
        url: cartoUrl('dark_all'),
        attribution: carto,
        subdomains: 'abcd',
      },
      carto_voyager: {
        url: cartoUrl('rastertiles/voyager'),
        attribution: carto,
        subdomains: 'abcd',
      },
    };

    // Keyless CARTO tiles still return HTTP 200 — but the PNG is a grey
    // "API KEY REQUIRED" watermark, so there is nothing to detect at runtime.
    // The only defence is this config-time check: fall back to OSM.
    if (!key && tile !== 'osm') {
      console.warn(
        '[StoreFinder] CARTO map styles need an API key. Add one in the section ' +
          'settings — falling back to OpenStreetMap tiles.'
      );
      return configs.osm;
    }

    return configs[tile] || configs.carto_light;
  }

  // ── Event binding ───────────────────────────────────────────

  _bindEvents() {
    // List item buttons → open popup + highlight
    this.el.querySelectorAll('.store-finder__list-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const index = parseInt(btn.dataset.index, 10);
        this._openMarker(index);
        this._setActiveListItem(index);

        // On mobile, scroll map into view
        if (window.innerWidth < 768 && this.mapEl) {
          this.mapEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    if (!this.searchInput) return;

    // Password managers treat this as an address field and autofill it. Holding
    // it readonly until first focus stops that. Applied here rather than as an
    // inline onfocus attribute so a strict script-src CSP can't strip it — and
    // so the field stays usable if this script never runs.
    this.searchInput.setAttribute('readonly', '');
    this.searchInput.addEventListener(
      'focus',
      () => this.searchInput.removeAttribute('readonly'),
      { once: true }
    );

    this.searchBtn.addEventListener('click', () => this._handleSearch());
    this.searchInput.addEventListener('keydown', e => {
      if (e.key === 'Enter') this._handleSearch();
    });
    this.searchClear.addEventListener('click', () => this._clearSearch());
  }

  // ── Search & distance ───────────────────────────────────────

  async _handleSearch() {
    const raw = this.searchInput.value.trim();
    if (!raw) return;

    const postcode = this._normalise(raw);

    this.searchError.hidden = true;
    this.searchBtn.disabled = true;
    this.searchBtn.textContent = '…';

    try {
      const res = await fetch(
        `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`
      );
      const json = await res.json();

      if (!res.ok || !json.result) throw new Error('Not found');

      this._sortByDistance(json.result.latitude, json.result.longitude);
      this.searchClear.hidden = false;
    } catch {
      this.searchError.textContent = 'Please enter a valid UK postcode.';
      this.searchError.hidden = false;
    } finally {
      this.searchBtn.disabled = false;
      this.searchBtn.textContent = this.data.searchBtnText || 'Search';
    }
  }

  _sortByDistance(userLat, userLng) {
    const ranked = this._validStores()
      .map(s => ({
        ...s,
        distance: this._haversine(userLat, userLng, parseFloat(s.lat), parseFloat(s.lng)),
      }))
      .sort((a, b) => a.distance - b.distance);

    // Reorder DOM + show distance labels
    ranked.forEach(store => {
      const item = this._itemFor(store.index);
      if (!item) return;

      this.listEl.appendChild(item); // moves to end → builds sorted order

      const distEl = item.querySelector('.store-finder__list-distance');
      distEl.textContent = `${store.distance.toFixed(1)} miles away`;
      distEl.hidden = false;
    });

    // Stockists with no coordinates have no distance to sort by. Park them at
    // the bottom — left alone they'd sit above the nearest match.
    this.stores
      .filter(s => !this._hasCoords(s))
      .forEach(store => {
        const item = this._itemFor(store.index);
        if (!item) return;
        item.classList.add('is-unresolved');
        this.listEl.appendChild(item);
      });

    this._updateCount(ranked.length, true);

    if (!this.map) return;

    // Pan map and show user pin
    this.map.setView([userLat, userLng], 10, { animate: true });

    if (this.userMarker) this.map.removeLayer(this.userMarker);
    this.userMarker = window.L.circleMarker([userLat, userLng], {
      radius: 8,
      fillColor: '#4285f4',
      color: '#fff',
      weight: 2.5,
      fillOpacity: 1,
      className: 'store-finder__user-marker',
    })
      .addTo(this.map)
      .bindPopup('Your location');
  }

  _clearSearch() {
    this.searchInput.value = '';
    this.searchClear.hidden = true;
    this.searchError.hidden = true;

    if (this.userMarker && this.map) {
      this.map.removeLayer(this.userMarker);
      this.userMarker = null;
    }

    // Hide distance labels
    this.el
      .querySelectorAll('.store-finder__list-distance')
      .forEach(el => (el.hidden = true));

    this.el
      .querySelectorAll('.store-finder__list-item')
      .forEach(el => el.classList.remove('is-unresolved'));

    // Restore original DOM order
    [...this.listEl.querySelectorAll('.store-finder__list-item')]
      .sort((a, b) => parseInt(a.dataset.index, 10) - parseInt(b.dataset.index, 10))
      .forEach(item => this.listEl.appendChild(item));

    this._updateCount(this._validStores().length);

    // Zoom back to fit all pins
    const validStores = this._validStores();
    if (this.map && validStores.length > 1) {
      this.map.fitBounds(this._bounds(validStores), { padding: [40, 40], animate: true });
    }
  }

  // ── Helpers ─────────────────────────────────────────────────

  _itemFor(index) {
    return this.listEl.querySelector(
      `.store-finder__list-item[data-index="${index}"]`
    );
  }

  _bounds(stores) {
    return window.L.latLngBounds(
      stores.map(s => [parseFloat(s.lat), parseFloat(s.lng)])
    );
  }

  _openMarker(index) {
    const marker = this.markers[index];
    if (!marker || !this.map) return;
    marker.openPopup();
    this.map.panTo(marker.getLatLng(), { animate: true });
  }

  _setActiveListItem(index) {
    this.el.querySelectorAll('.store-finder__list-item').forEach(item => {
      item.classList.toggle(
        'is-active',
        parseInt(item.dataset.index, 10) === index
      );
    });

    const activeItem = this._itemFor(index);
    if (activeItem) {
      activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /** Counts what is actually plotted, not how many blocks exist. */
  _updateCount(count, searched = false) {
    if (!this.countEl) return;
    const noun = `stockist${count !== 1 ? 's' : ''}`;
    this.countEl.textContent = searched
      ? `${count} ${noun} found`
      : `${count} ${noun}`;
  }

  /** Number.isFinite, not truthiness — a longitude of 0 is valid in the UK. */
  _hasCoords(store) {
    return (
      Number.isFinite(parseFloat(store.lat)) &&
      Number.isFinite(parseFloat(store.lng))
    );
  }

  _validStores() {
    return this.stores.filter(s => this._hasCoords(s));
  }

  /** Uppercase, no spaces — the form postcodes.io echoes back and we cache by. */
  _normalise(postcode) {
    return String(postcode || '').toUpperCase().replace(/\s+/g, '');
  }

  /** Only http(s) links reach an href — blocks javascript: and data: URLs. */
  _safeUrl(url) {
    return /^https?:\/\//i.test(String(url || ''));
  }

  /** Haversine distance in miles */
  _haversine(lat1, lng1, lat2, lng2) {
    const R = 3958.8;
    const toRad = d => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /** Minimal HTML escaping for popup content */
  _esc(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  destroy() {
    this.destroyed = true;
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }
}

StoreFinder.CACHE_KEY = 'store-finder-geo-v1';
StoreFinder.CACHE_TTL = 90 * 24 * 60 * 60 * 1000; // 90 days
StoreFinder.BULK_LIMIT = 100; // postcodes.io hard cap per bulk request

// ── Bootstrap ────────────────────────────────────────────────

function initSection(el) {
  const sectionId = el.dataset.storeFinderSection;
  if (!sectionId || el.dataset.sfInitialized) return;

  const dataEl = document.getElementById(`store-finder-data-${sectionId}`);
  if (!dataEl) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (err) {
    console.error('[StoreFinder] Could not parse section data:', err);
    return;
  }

  // Destroy any previous instance (theme editor reloads)
  if (storeFinderInstances.has(sectionId)) {
    storeFinderInstances.get(sectionId).destroy();
  }

  el.dataset.sfInitialized = 'true';
  storeFinderInstances.set(sectionId, new StoreFinder(sectionId, data, el));
}

/** Hold off on Leaflet until the section is nearly in view. */
function observe(el) {
  if (!('IntersectionObserver' in window)) {
    initSection(el);
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        initSection(entry.target);
      });
    },
    { rootMargin: '300px' }
  );

  observer.observe(el);
}

function observeAll(root = document) {
  root.querySelectorAll('[data-store-finder-section]').forEach(observe);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => observeAll());
} else {
  observeAll();
}

// Shopify theme editor: reinit when a section is reloaded
document.addEventListener('shopify:section:load', e => {
  const el = e.target.querySelector('[data-store-finder-section]');
  if (!el) return;
  delete el.dataset.sfInitialized;
  initSection(el); // already on screen in the editor — no need to wait
});

// …and tear the old map down so repeated edits don't leak instances
document.addEventListener('shopify:section:unload', e => {
  const el = e.target.querySelector('[data-store-finder-section]');
  if (!el) return;

  const sectionId = el.dataset.storeFinderSection;
  if (storeFinderInstances.has(sectionId)) {
    storeFinderInstances.get(sectionId).destroy();
    storeFinderInstances.delete(sectionId);
  }
});
